import React from 'react';
import clsx from 'clsx';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import {
  Pencil, Trash2, X, MoreVertical, Clock, MapPin, AlignLeft, UserRound, Repeat, CheckCircle2, Ban, RotateCcw,
  ExternalLink, Copy, Timer, Users, Bell, Send, Mail as MailIcon,
} from 'lucide-react';
import { attendeeStatus, LINK_KINDS } from './InviteFields';
import { fmtRange, fmtTime, fmtLongDate, isAllDayLike, addDays, weekdayName, parseYmd, shortMonth } from './calendarMath';
import { kindOf, reminderText } from './palette';
import { Button, Pill } from '../kit';

// "Every week on Friday, until 18 Dec 2026"
export function repeatText(o) {
  if (!o || !o.repeat || o.repeat === 'none') return null;
  const s = o.start || new Date(o.start_at);
  const base = {
    daily: 'Every day', weekdays: 'Every weekday', weekly: `Every week on ${weekdayName(s.getDay())}`,
    fortnightly: `Every fortnight on ${weekdayName(s.getDay())}`, monthly: `Every month on day ${s.getDate()}`,
  }[o.repeat];
  const u = o.repeat_until && parseYmd(String(o.repeat_until).slice(0, 10));
  return u ? `${base}, until ${u.getDate()} ${shortMonth(u.getMonth())} ${u.getFullYear()}` : base;
}

export function whenText(o) {
  if (isAllDayLike(o)) {
    const last = o.end.getHours() === 0 && o.end.getMinutes() === 0 ? addDays(o.end, -1) : o.end;
    return last.toDateString() === o.start.toDateString() ? fmtLongDate(o.start) : `${fmtLongDate(o.start)} – ${fmtLongDate(last)}`;
  }
  if (o.start.toDateString() !== o.end.toDateString()) return `${fmtLongDate(o.start)}, ${fmtTime(o.start)} – ${fmtLongDate(o.end)}, ${fmtTime(o.end)}`;
  return `${fmtLongDate(o.start)} · ${fmtRange(o.start, o.end)}`;
}

// The details card for one appointment or key date.
export default function EventCard({ occ, color, onClose, onEdit, onDelete, onStatus, onLogTime, onOpenFile, onDuplicate, onTaskDone, canLog, onResend, scope = 'sc' }) {
  const key = occ.kind === 'keydate';
  const k = key ? null : kindOf(occ.kind);
  const KindIcon = key ? occ.icon : k.icon;
  const rpt = repeatText(occ);
  const cancelled = occ.status === 'cancelled';
  const done = occ.status === 'completed';
  const mapUrl = occ.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(occ.location)}` : null;

  return (
    <div className="text-slate-800">
      <div className="flex items-center justify-end gap-0.5 px-2 pt-2">
        {!key && <IconBtn label="Edit appointment" onClick={onEdit}><Pencil size={16} /></IconBtn>}
        {!key && <IconBtn label="Delete appointment" onClick={onDelete}><Trash2 size={16} /></IconBtn>}
        {!key && (
          <RMenu.Root>
            <RMenu.Trigger asChild>
              <button type="button" aria-label="More actions" className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                <MoreVertical size={16} aria-hidden />
              </button>
            </RMenu.Trigger>
            <RMenu.Portal>
              <RMenu.Content align="end" sideOffset={4} className="z-[90] min-w-[210px] bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1">
                {!done && <MenuItem icon={CheckCircle2} onSelect={() => onStatus('completed')}>Mark as done</MenuItem>}
                {!cancelled && <MenuItem icon={Ban} onSelect={() => onStatus('cancelled')}>Mark as cancelled</MenuItem>}
                {(done || cancelled) && <MenuItem icon={RotateCcw} onSelect={() => onStatus('scheduled')}>Back to booked</MenuItem>}
                <MenuItem icon={Copy} onSelect={onDuplicate}>Duplicate</MenuItem>
                {(occ.attendees || []).length > 0 && onResend && <MenuItem icon={Send} onSelect={onResend}>Send the invite again</MenuItem>}
              </RMenu.Content>
            </RMenu.Portal>
          </RMenu.Root>
        )}
        <IconBtn label="Close" onClick={onClose}><X size={17} /></IconBtn>
      </div>

      <div className="px-5 pb-5 pt-1 space-y-3.5">
        <div className="flex gap-3.5">
          <span className="mt-1.5 w-3.5 h-3.5 rounded-[4px] flex-shrink-0" style={{ background: color.hex }} aria-hidden />
          <div className="min-w-0">
            <h2 className={clsx('text-[20px] leading-7 font-semibold text-slate-900 break-words', cancelled && 'line-through text-slate-500')}>{occ.title}</h2>
            <p className="text-[13.5px] text-slate-600 mt-0.5">{whenText(occ)}</p>
            {rpt && <p className="text-[12.5px] text-slate-500 mt-0.5 flex items-center gap-1"><Repeat size={12} aria-hidden />{rpt}</p>}
            {(done || cancelled) && <div className="mt-1.5"><Pill status={occ.status} label={done ? 'Done' : 'Cancelled'} size="xs" /></div>}
          </div>
        </div>

        <Row icon={KindIcon || Users}>{key ? occ.typeLabel : k.label}</Row>
        {occ.participant && (
          <Row icon={UserRound}>
            <span className="font-medium text-slate-800">{occ.participant}</span>
            {scope === 'email' && LINK_KINDS[occ.module] && <span className={clsx('ml-1.5 px-1.5 py-px rounded text-[10.5px] font-semibold ring-1', LINK_KINDS[occ.module].chip)}>{LINK_KINDS[occ.module].short}</span>}
            {(occ.coordination_id || (scope === 'email' && occ.client_id)) && (
              <button type="button" onClick={onOpenFile} className="ml-2 inline-flex items-center gap-1 text-[12.5px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline">
                Open file <ExternalLink size={11} aria-hidden />
              </button>
            )}
          </Row>
        )}
        {!key && <Row icon={Users}>{occ.coordinator_name || (scope === 'email' ? 'Not on anyone’s calendar' : 'No coordinator')}</Row>}
        {!key && (occ.attendees || []).length > 0 && (
          <Row icon={MailIcon}>
            <p className="text-[12.5px] text-slate-500 mb-1">
              {occ.invites_sent_at ? 'Invited' : 'Not invited yet'} · {(occ.attendees || []).filter((a) => a.status === 'accepted').length} yes,{' '}
              {(occ.attendees || []).filter((a) => a.status === 'declined').length} no, {(occ.attendees || []).filter((a) => !a.status || a.status === 'needs-action').length} waiting
            </p>
            <ul className="space-y-1">
              {occ.attendees.map((a) => {
                const S = attendeeStatus(a.status);
                return (
                  <li key={a.email} className="flex items-center gap-2 text-[13px]">
                    <S.icon size={14} className={S.cls} aria-hidden />
                    <span className="truncate text-slate-800">{a.name || a.email}</span>
                    <span className="ml-auto text-[11.5px] text-slate-500 flex-shrink-0">{S.label}</span>
                  </li>
                );
              })}
            </ul>
          </Row>
        )}
        {occ.location && (
          <Row icon={MapPin}>
            <a href={mapUrl} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-indigo-700 hover:underline break-words">{occ.location}</a>
          </Row>
        )}
        {!key && occ.reminder_minutes != null && occ.coordinator_id && <Row icon={Bell}>{reminderText(occ.reminder_minutes)}, by email</Row>}
        {occ.notes && <Row icon={AlignLeft}><span className="whitespace-pre-wrap break-words">{occ.notes}</span></Row>}
        {!key && occ.time_entry_id && <Row icon={Timer}><span className="text-emerald-700 font-medium">Time logged for this appointment</span></Row>}
        {key && <p className="text-[12px] text-slate-500 pl-8">Set on the participant’s file. Change it there.</p>}

        <div className="flex flex-wrap gap-2 pt-1 pl-8">
          {!key && canLog && !occ.time_entry_id && !cancelled && occ.coordination_id && (
            <Button size="sm" icon={Clock} onClick={onLogTime}>Log time</Button>
          )}
          {key && occ.type === 'task' && <Button size="sm" variant="secondary" icon={CheckCircle2} onClick={onTaskDone}>Mark task done</Button>}
          {key && occ.coordination_id && <Button size="sm" variant="secondary" icon={ExternalLink} onClick={onOpenFile}>Open file</Button>}
          {!key && !cancelled && (occ.attendees || []).length > 0 && !occ.invites_sent_at && onResend && <Button size="sm" variant="secondary" icon={Send} onClick={onResend}>Send invites</Button>}
        </div>
      </div>
    </div>
  );
}

function IconBtn({ label, onClick, children }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick}
      className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
      {React.cloneElement(children, { 'aria-hidden': true })}
    </button>
  );
}
function MenuItem({ icon: Icon, onSelect, children }) {
  return (
    <RMenu.Item onSelect={onSelect} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">
      <Icon size={14} aria-hidden className="opacity-70" />{children}
    </RMenu.Item>
  );
}
function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-3.5 text-[13.5px] text-slate-700">
      <Icon size={17} className="mt-0.5 text-slate-400 flex-shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
